import React, { useEffect, useRef } from 'react';
import type { Detection } from '../../hooks/useObjectDetector';
import { salienceColor } from '../../engine/GazeObjectMatcher';

/**
 * Draws object detection bounding boxes on a canvas overlay stacked on
 * top of the webcam + face-mesh overlay. Uses its own canvas to avoid
 * clearing conflicts with the face-mesh RAF loop.
 *
 * The canvas mirrors the video horizontally (CSS scaleX(-1) on the video
 * element means we need to draw in mirrored coordinates).
 */
export function DetectionOverlay({
  detections,
  gazeMatches,
}: {
  detections: Detection[];
  gazeMatches?: Set<string>; // labels of objects being looked at
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Match the face-mesh overlay canvas dimensions (set by useFaceLandmarker)
    const overlay = document.getElementById('overlayCanvas') as HTMLCanvasElement | null;
    const videoWidth = overlay?.width || 640;
    const videoHeight = overlay?.height || 480;
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Mirror to match the mirrored video
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);

    for (const det of detections) {
      const { bbox, labelKo, score, salience, label } = det;
      const color = salienceColor(salience);
      const isLookedAt = gazeMatches?.has(label) ?? false;
      const lineWidth = isLookedAt ? 3 : 2;
      const alpha = isLookedAt ? 1 : 0.7;

      // Bounding box
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.globalAlpha = alpha;
      ctx.setLineDash(salience === 'high' ? [] : [6, 4]);
      ctx.strokeRect(bbox.x, bbox.y, bbox.w, bbox.h);

      // Semi-transparent fill for high-salience items being looked at
      if (isLookedAt && salience === 'high') {
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.12;
        ctx.fillRect(bbox.x, bbox.y, bbox.w, bbox.h);
      }

      ctx.globalAlpha = 1;
      ctx.setLineDash([]);

      // Label background
      const text = `${labelKo} ${Math.round(score * 100)}%`;
      ctx.font = '600 12px Pretendard, sans-serif';
      const tm = ctx.measureText(text);
      const labelH = 20;
      const labelW = tm.width + 10;
      const labelX = bbox.x;
      const labelY = bbox.y - labelH - 2;

      ctx.fillStyle = color;
      ctx.globalAlpha = 0.85;
      ctx.fillRect(labelX, labelY, labelW, labelH);
      ctx.globalAlpha = 1;

      // Label text
      ctx.fillStyle = '#fff';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, labelX + 5, labelY + labelH / 2);

      // "Looking at" indicator
      if (isLookedAt) {
        ctx.font = '700 11px Pretendard, sans-serif';
        ctx.fillStyle = '#fff';
        const alertText = '👁 주시 중';
        const atm = ctx.measureText(alertText);
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.85;
        ctx.fillRect(labelX + labelW + 4, labelY, atm.width + 10, labelH);
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#fff';
        ctx.fillText(alertText, labelX + labelW + 9, labelY + labelH / 2);
      }
    }

    ctx.restore();
  }, [detections, gazeMatches]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 5,
      }}
    />
  );
}
